import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Check, ChevronDown, Copy, Loader2, RefreshCw, Sparkles, X } from 'lucide-react'

import { Button } from 'components/ui/button'
import { cn } from 'lib/utils'
import { summariseChat, type ChatSummary, type ChatSummaryEntry } from 'services/aiService'

import { isAskMdtAvailable } from '../availability'

const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`

const day = (iso: string) => new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })

/** "Your 12 unread messages, Oct 14 to Oct 16" or "The latest 100 messages, Oct 1 to Oct 16". */
export const windowLabel = ({ kind, count, from, to }: ChatSummary['window']): string => {
	const span = day(from) === day(to) ? day(from) : `${day(from)} to ${day(to)}`
	return `${kind === 'unread' ? `Your ${plural(count, 'unread message')}` : `The latest ${plural(count, 'message')}`}, ${span}`
}

const messageOf = (error: any): string => error?.response?.data?.message || "Ask MDT couldn't summarise this conversation just now. Try again in a moment."

/** One entry, with the messages it rests on behind a toggle. */
function Entry({ entry, owner }: { entry: ChatSummaryEntry; owner?: string }) {
	const [open, setOpen] = useState(false)
	return (
		<li className="text-sm text-gray-800">
			<div className="flex items-start gap-1">
				<span className="min-w-0 flex-1">
					{owner ? <span className="font-medium">{owner}: </span> : null}
					{entry.text}
				</span>
				<button
					type="button"
					onClick={() => setOpen(value => !value)}
					aria-expanded={open}
					aria-label={open ? 'Hide the messages behind this' : 'Show the messages behind this'}
					className="shrink-0 rounded p-0.5 text-gray-400 hover:text-primary"
				>
					<ChevronDown className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-180')} aria-hidden="true" />
				</button>
			</div>
			{open && (
				<ul className="mt-1 space-y-1 border-l-2 border-gray-100 pl-2">
					{entry.evidence.map(message => (
						<li key={message.id} className="text-xs text-gray-600">
							<span className="font-medium text-gray-700">{message.sender}</span>, {day(message.at)}: {message.text}
						</li>
					))}
				</ul>
			)}
		</li>
	)
}

function Section({ title, entries, owners = false }: { title: string; entries: (ChatSummaryEntry & { owner?: string })[]; owners?: boolean }) {
	if (!entries.length) return null
	return (
		<div>
			<h5 className="text-xs font-semibold uppercase tracking-wide text-gray-500">{title}</h5>
			<ul className="mt-1 space-y-1">
				{entries.map((entry, index) => (
					<Entry key={index} entry={entry} owner={owners ? entry.owner : undefined} />
				))}
			</ul>
		</div>
	)
}

/**
 * "Summarise" on a chat conversation (MYD-650): what was decided, what is open
 * and who owes what, for someone catching up. The messages go to the model
 * only when this is clicked, which the bar says; nothing is saved.
 *
 * Inline rather than a dialog: the chat popup closes on a click outside it, and
 * a dialog's portal is outside it.
 *
 * Renders nothing for a user without Ask MDT, or with no conversation open.
 */
export function ChatSummaryPanel({
	conversationKey,
	className,
	summarise = summariseChat,
	available,
}: {
	/** A DM thread id, a project id, or `portfolio:<id>`. */
	conversationKey: string
	className?: string
	summarise?: typeof summariseChat
	/** Overrides the Ask MDT availability check, for tests. */
	available?: boolean
}) {
	const user = useSelector((state: any) => state.userLogin?.userInfo)
	const [result, setResult] = useState<ChatSummary | null>(null)
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [copied, setCopied] = useState(false)

	// A different conversation starts over: a summary belongs to one.
	useEffect(() => {
		setResult(null)
		setError(null)
		setCopied(false)
	}, [conversationKey])

	if (!(available ?? isAskMdtAvailable(user)) || !conversationKey) return null

	const run = async () => {
		setBusy(true)
		setError(null)
		setCopied(false)
		try {
			setResult(await summarise(conversationKey))
		} catch (failure) {
			setError(messageOf(failure))
		} finally {
			setBusy(false)
		}
	}

	const copy = async () => {
		if (!result) return
		try {
			await navigator.clipboard.writeText(result.text)
			setCopied(true)
		} catch {
			setError('Copy did not work here: select the text and copy it instead.')
		}
	}

	if (!result) {
		return (
			<div className={cn('flex shrink-0 flex-wrap items-center gap-2 border-b border-gray-100 px-3 py-1.5', className)}>
				<p className="min-w-0 flex-1 text-xs text-gray-500">Sends these messages to Claude. Nothing is saved.</p>
				<Button type="button" variant="ghost" size="input" onClick={run} disabled={busy}>
					{busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <Sparkles className="mr-1 h-3.5 w-3.5 text-primary" aria-hidden="true" />}
					{busy ? 'Summarising…' : 'Summarise'}
				</Button>
				{error && (
					<p role="alert" className="w-full text-xs text-destructive">
						{error}
					</p>
				)}
			</div>
		)
	}

	return (
		<section aria-label="Conversation summary" className={cn('max-h-72 shrink-0 overflow-y-auto border-b border-gray-200 bg-white px-3 py-2', className)}>
			<div className="flex flex-wrap items-start justify-between gap-2">
				<div className="min-w-0">
					<h4 className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
						<Sparkles className="h-4 w-4 text-primary" aria-hidden="true" />
						Summary
					</h4>
					<p className="mt-0.5 text-xs text-gray-500">{windowLabel(result.window)}. AI summary: check it against the messages.</p>
				</div>
				<div className="flex items-center gap-1">
					<Button type="button" variant="ghost" size="input" onClick={copy}>
						{copied ? <Check className="mr-1 h-3.5 w-3.5" aria-hidden="true" /> : <Copy className="mr-1 h-3.5 w-3.5" aria-hidden="true" />}
						{copied ? 'Copied' : 'Copy'}
					</Button>
					<Button type="button" variant="ghost" size="input" onClick={run} disabled={busy} aria-label="Summarise again">
						{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />}
					</Button>
					<Button type="button" variant="ghost" size="input" onClick={() => setResult(null)} aria-label="Close the summary">
						<X className="h-3.5 w-3.5" aria-hidden="true" />
					</Button>
				</div>
			</div>
			{error && (
				<p role="alert" className="mt-1 text-xs text-destructive">
					{error}
				</p>
			)}
			<div className="mt-2 space-y-3">
				{result.overview.length > 0 && <p className="text-sm text-gray-800">{result.overview.map(entry => entry.text).join(' ')}</p>}
				<Section title="Decisions" entries={result.decisions} />
				<Section title="Open questions" entries={result.openQuestions} />
				<Section title="Action items" entries={result.actionItems} owners />
			</div>
		</section>
	)
}

export default ChatSummaryPanel
